/**
 * Universal Media Player System
 * 
 * This module provides a unified media viewing/playing experience
 * that can be used across both Messages and Posts.
 * 
 * Components:
 * - MediaViewer: Core media rendering component
 * - MediaCarousel: Multi-media carousel with navigation
 * - MediaModal: Full-screen media modal viewer
 * 
 * Usage Examples:
 * 
 * // Single media item
 * import { MediaViewer } from '@/components/common/MediaPlayer';
 * <MediaViewer src={url} type="image" onClick={handleClick} />
 * 
 * // Media carousel
 * import { MediaCarousel } from '@/components/common/MediaPlayer';
 * <MediaCarousel 
 *   mediaItems={items} 
 *   onMediaClick={handleMediaClick}
 *   showPlayControls={true}
 * />
 * 
 * // Modal viewer
 * import { MediaModal } from '@/components/common/MediaPlayer';
 * <MediaModal 
 *   isOpen={isOpen}
 *   media={selectedMedia}
 *   onClose={handleClose}
 * />
 */

export { default as MediaViewer } from './MediaViewer';
export { default as MediaCarousel } from './MediaCarousel';
export { default as MediaModal } from './MediaModal';

// Hook for media player state management
export { useMediaPlayer } from './hooks/useMediaPlayer';

// Utility functions
export const getMediaType = (file) => {
  if (!file) return 'image';
  
  // Check multiple possible type fields
  const type = file.type || file.mediaType || file.mimeType || '';
  // Check multiple possible name/filename fields
  const name = file.name || file.fileName || file.originalName || '';
  // Also check the URL for file extension
  const url = file.url || file.src || file.displayUrl || '';
  
  // Video formats - check type, filename, and URL
  if (type.startsWith('video/') || 
      name.match(/\.(mp4|webm|ogg|mov|avi|mkv|3gp|wmv|flv)$/i) ||
      url.match(/\.(mp4|webm|ogg|mov|avi|mkv|3gp|wmv|flv)(\?|$)/i)) {
    return 'video';
  }
  
  // Audio formats - check type, filename, and URL  
  if (type.startsWith('audio/') || 
      name.match(/\.(mp3|wav|ogg|m4a|flac|aac|wma|opus)$/i) ||
      url.match(/\.(mp3|wav|ogg|m4a|flac|aac|wma|opus)(\?|$)/i)) {
    return 'audio';
  }
  
  return 'image';
};

export const getMediaUrl = (media) => {
  // NEW: Support simplified multi-quality video structure
  // For videos, use the first available quality as the default URL
  if (media?.qualityUrls && typeof media.qualityUrls === 'object') {
    const qualities = media.availableQualities || Object.keys(media.qualityUrls);
    if (qualities.length > 0) {
      const firstQuality = qualities[0];
      const url = media.qualityUrls[firstQuality];
      if (url && typeof url === 'string') {
        return url.trim();
      }
    }
  }
  
  // Legacy support: existing field names for backward compatibility
  // Return empty string if no valid URL is found (for security - locked content won't have URLs)
  const url = media?.displayUrl || media?.fileUrl || media?.url || media?.src || media?.preview || '';
  return url && typeof url === 'string' ? url.trim() : '';
};

export const getQualityUrls = (media) => {
  // NEW: Extract quality URLs from the simplified structure
  if (media?.qualityUrls && typeof media.qualityUrls === 'object') {
    return media.qualityUrls;
  }
  
  // Legacy: Extract from videoQualities array for backward compatibility
  if (media?.videoQualities && Array.isArray(media.videoQualities)) {
    return media.videoQualities.reduce((acc, quality) => {
      if (quality.quality && quality.displayUrl) {
        acc[quality.quality] = quality.displayUrl;
      }
      return acc;
    }, {});
  }
  
  return {};
};

export const getVideoSources = (media) => {
  // Return array of VideoQuality objects for VideoPlayerSkin
  if (media?.videoQualities && Array.isArray(media.videoQualities)) {
    return media.videoQualities;
  }
  
  return [];
};

export const formatMediaForCarousel = (mediaArray) => {
  return mediaArray.map((media, index) => {
    // Get the primary URL first
    const primaryUrl = getMediaUrl(media);
    
    // Create an enhanced media object for type detection
    const enhancedMedia = {
      ...media,
      url: primaryUrl // Add URL to the object for type detection
    };
    
    return {
      ...media,
      type: getMediaType(enhancedMedia), // Pass enhanced object with URL
      url: primaryUrl, // Primary URL (first available quality for videos)
      qualityUrls: getQualityUrls(media), // Multi-quality URL mapping
      availableQualities: media.availableQualities || [], // Simplified quality array
      videoSources: getVideoSources(media), // Legacy array of VideoQuality objects
      id: media.id || `media-${index}`,
      isFreePreview: media.isFreePreview || media.isPreview || false // Map backend isPreview to frontend isFreePreview
    };
  });
};
